import React, { useState, useEffect, useCallback } from 'react';
import { 
  fetchEmailConfig, 
  updateEmailConfig, 
  sendTestEmail,
  EmailConfigResponse,
  EmailTemplate as EmailTemplateType,
  EmailType,
  EMAIL_TYPES
} from '../../api/emailConfig';

interface EmailConfigProps {
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const TestEmailButton: React.FC = () => {
  const [isSending, setIsSending] = useState(false);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Email validation function
  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Handle email input change
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setTestEmail(email);
    
    if (email && !isValidEmail(email)) {
      setValidationError('Please enter a valid email address');
    } else {
      setValidationError(null);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      setTestResult('Please enter an email address');
      return;
    }

    if (!isValidEmail(testEmail)) {
      setTestResult('Please enter a valid email address');
      return;
    }

    setIsSending(true);
    setTestResult(null);
    
    try {
      await sendTestEmail(testEmail);
      setTestResult('Test email sent successfully!');
      setTestEmail(''); // Clear the input after success
      setValidationError(null);
    } catch (error: any) {
      console.error('Email sending error:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to send test email';
      setTestResult(`Error: ${errorMessage}`);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex flex-col space-y-4">
      <div className="flex space-x-4">
        <input
          type="email"
          value={testEmail}
          onChange={handleEmailChange}
          placeholder="Enter email address"
          className="theme-input flex-1 p-2 border rounded-lg"
          disabled={isSending}
        />
        <button
          onClick={handleTestEmail}
          disabled={isSending || !!validationError}
          className={`px-4 py-2 text-white rounded-md ${
            isSending || !!validationError
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-blue-500 hover:bg-blue-600'
          }`}
        >
          {isSending ? 'Sending...' : 'Send Test Email'}
        </button>
      </div>
      {validationError && (
        <p className="text-red-500 text-sm">{validationError}</p>
      )}
      {testResult && (
        <p className={`text-sm ${
          testResult.startsWith('Error') ? 'text-red-500' : 'text-green-500'
        }`}>
          {testResult}
        </p>
      )}
    </div>
  );
};

const BrevoSettings: React.FC<{
  brevoApiKeyConfigured: boolean;
  newApiKey: string;
  senderName: string;
  senderEmail: string;
  onApiKeyChange: (value: string) => void;
  onSenderNameChange: (value: string) => void;
  onSenderEmailChange: (value: string) => void;
  onSave: () => void;
}> = ({ brevoApiKeyConfigured, newApiKey, senderName, senderEmail, onApiKeyChange, onSenderNameChange, onSenderEmailChange, onSave }) => (
  <div className="glass-card p-6 rounded-lg mb-6">
    <h3 className="text-lg font-medium theme-text-primary mb-4">Brevo Email Settings</h3>
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">
          API Key{' '}
          <span className={brevoApiKeyConfigured ? 'text-green-400' : 'text-gray-400'}>
            {brevoApiKeyConfigured ? 'Configured ✓' : 'Not configured'}
          </span>
        </label>
        <input
          type="password"
          value={newApiKey}
          onChange={(e) => onApiKeyChange(e.target.value)}
          placeholder="Leave blank to keep the existing API key"
          className="theme-input w-full p-2 border rounded-lg"
        />
        <p className="text-xs theme-text-secondary mt-1">
          Enter a new value only to rotate/replace the stored Brevo API key. It is never shown here once saved.
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">Sender Name</label>
        <input
          type="text"
          value={senderName}
          onChange={(e) => onSenderNameChange(e.target.value)}
          className="theme-input w-full p-2 border rounded-lg"
        />
      </div>
      <div>
        <label className="block text-sm font-medium theme-text-primary mb-2">Sender Email</label>
        <input
          type="email"
          value={senderEmail}
          onChange={(e) => onSenderEmailChange(e.target.value)}
          className="theme-input w-full p-2 border rounded-lg"
        />
      </div>
      <button
        onClick={onSave}
        className="theme-button-primary w-full px-4 py-2 rounded-lg"
      >
        Save Email Settings
      </button>
    </div>
  </div>
);

const EmailTemplateForm: React.FC<{
  template: EmailTemplateType;
  onUpdate: (field: string, value: string | boolean | string[]) => void;
  onSave: () => void;
  onCancel: () => void;
}> = ({ template, onUpdate, onSave, onCancel }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium theme-text-primary mb-2">Name</label>
      <input
        type="text"
        value={template.name}
        onChange={(e) => onUpdate('name', e.target.value)}
        className="theme-input w-full p-2 border rounded-lg"
      />
    </div>

    <div>
      <label className="block text-sm font-medium theme-text-primary mb-2">Type</label>
      <select
        value={template.type}
        onChange={(e) => onUpdate('type', e.target.value as EmailType)}
        className="theme-input w-full p-2 border rounded-lg"
      >
        {Object.entries(EMAIL_TYPES).map(([key, label]) => (
          <option key={key} value={key}>
            {label}
          </option>
        ))}
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium theme-text-primary mb-2">Subject</label>
      <input
        type="text"
        value={template.subject}
        onChange={(e) => onUpdate('subject', e.target.value)}
        className="theme-input w-full p-2 border rounded-lg"
      />
    </div>

    <div>
      <label className="block text-sm font-medium theme-text-primary mb-2">Body</label>
      <textarea
        value={template.body}
        onChange={(e) => onUpdate('body', e.target.value)}
        className="theme-input w-full p-2 border rounded-lg"
        rows={6}
      />
    </div>

    <div>
      <label className="block text-sm font-medium theme-text-primary mb-2">Audience</label>
      <div className="mt-1">
        <div className="flex flex-wrap gap-2">
          {['Admin', 'Assessor', 'Counsellor', 'Client'].map((audienceType) => (
            <div key={audienceType} className="flex items-center">
              <input
                type="checkbox"
                id={`audience-${audienceType}`}
                checked={template.audience?.includes(audienceType) || false}
                onChange={(e) => {
                  const currentAudience = template.audience || [];
                  const newAudience = e.target.checked
                    ? [...currentAudience, audienceType]
                    : currentAudience.filter(a => a !== audienceType);
                  onUpdate('audience', newAudience);
                }}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor={`audience-${audienceType}`} className="ml-2 block text-sm text-gray-700">
                {audienceType}
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="flex justify-end space-x-3">
      <button
        onClick={onCancel}
        className="px-4 py-2 border border-white/20 rounded-lg text-sm font-medium theme-text-primary hover:bg-white/10 transition-colors"
      >
        Cancel
      </button>
      <button
        onClick={onSave}
        className="theme-button-primary px-4 py-2 rounded-lg text-sm font-medium"
      >
        Save
      </button>
    </div>
  </div>
);

const EmailTemplateList: React.FC<{
  templates: EmailTemplateType[];
  onEdit: (template: EmailTemplateType) => void;
  onToggle: (template: EmailTemplateType) => void;
}> = ({ templates, onEdit, onToggle }) => {
  if (!Array.isArray(templates) || templates.length === 0) {
    return <div className="theme-text-secondary text-center">No email templates found</div>;
  }

  return (
    <div className="mt-8 flow-root">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <div className="overflow-hidden shadow ring-1 ring-white/10 sm:rounded-lg">
            <table className="min-w-full divide-y divide-white/10">
              <thead className="glass-card-light">
                <tr>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold theme-text-primary">
                    Name
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold theme-text-primary">
                    Type
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold theme-text-primary">
                    Audience
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold theme-text-primary">
                    Status
                  </th>
                  <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {templates.map((template) => (
                  <tr key={template._id}>
                    <td className="whitespace-nowrap px-3 py-4 text-sm theme-text-primary">
                      {template.name}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm theme-text-secondary">
                      {EMAIL_TYPES[template.type as keyof typeof EMAIL_TYPES]}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm theme-text-secondary">
                      {template.audience && template.audience.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {template.audience.map((audience) => (
                            <span key={audience} className="inline-flex rounded-full px-2 text-xs font-semibold leading-5 bg-blue-500/20 text-blue-300">
                              {audience}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="theme-text-secondary">All</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm theme-text-secondary">
                      <span className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                        template.active 
                          ? 'bg-green-500/20 text-green-300' 
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => onEdit(template)}
                          className="text-blue-300 hover:text-blue-200 font-semibold transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onToggle(template)}
                          className={`font-semibold transition-colors ${
                            template.active 
                              ? 'text-red-300 hover:text-red-200' 
                              : 'text-green-300 hover:text-green-200'
                          }`}
                        >
                          {template.active ? 'Deactivate' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmailConfig: React.FC<EmailConfigProps> = ({ onSuccess, onError }) => {
  const [config, setConfig] = useState<EmailConfigResponse>({
    brevoApiKeyConfigured: false,
    senderName: '',
    senderEmail: ''
  });
  // Write-only: the server never returns the real API key, so this always
  // starts empty and is only submitted (replacing the stored key) if the
  // admin actually types a new value.
  const [newApiKey, setNewApiKey] = useState('');

  const [templates, setTemplates] = useState<EmailTemplateType[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplateType | null>(null);
  const [loading, setLoading] = useState(true);

  const loadEmailConfig = useCallback(async () => {
    try {
      const data = await fetchEmailConfig();
      setConfig({
        brevoApiKeyConfigured: data.brevoApiKeyConfigured || false,
        senderName: data.senderName || '',
        senderEmail: data.senderEmail || '',
        _id: data._id
      });
      setTemplates(data.templates || []); // Ensure templates is always an array
      setLoading(false);
    } catch (error) {
      console.error('Error loading email config:', error);
      onError('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadEmailConfig();
  }, [loadEmailConfig]);

  if (loading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Brevo Settings Section */}
      <div className="glass-card-light rounded-lg">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium theme-text-primary">Brevo API Settings</h3>
        </div>
        <div className="p-6">
          <BrevoSettings
            brevoApiKeyConfigured={config.brevoApiKeyConfigured}
            newApiKey={newApiKey}
            senderName={config.senderName}
            senderEmail={config.senderEmail}
            onApiKeyChange={setNewApiKey}
            onSenderNameChange={(value) => setConfig({ ...config, senderName: value })}
            onSenderEmailChange={(value) => setConfig({ ...config, senderEmail: value })}
            onSave={async () => {
              try {
                await updateEmailConfig({
                  senderName: config.senderName,
                  senderEmail: config.senderEmail,
                  ...(newApiKey ? { brevoApiKey: newApiKey } : {})
                });
                setNewApiKey('');
                onSuccess('Email configuration updated successfully');
                loadEmailConfig();
              } catch (error) {
                onError('Failed to update email configuration');
              }
            }}
          />
        </div>
      </div>

      {/* Test Email Section */}
      <div className="glass-card-light rounded-lg">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium theme-text-primary">Test Email Configuration</h3>
        </div>
        <div className="p-6">
          <TestEmailButton />
        </div>
      </div>

      {/* Email Templates Section */}
      <div className="glass-card-light rounded-lg">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-lg font-medium theme-text-primary">Email Templates</h3>
        </div>
        <div className="p-6">
          {editingTemplate ? (
            <EmailTemplateForm
              template={editingTemplate}
              onUpdate={(field, value) =>
                setEditingTemplate({ ...editingTemplate, [field]: value })
              }
              onSave={async () => {
                try {
                  // Save template logic
                  const updatedTemplates = templates.map(t =>
                    t._id === editingTemplate._id ? editingTemplate : t
                  );
                  setTemplates(updatedTemplates);
                  setEditingTemplate(null);
                  onSuccess('Template updated successfully');
                } catch (error) {
                  onError('Failed to update template');
                }
              }}
              onCancel={() => setEditingTemplate(null)}
            />
          ) : (
            <EmailTemplateList
              templates={templates}
              onEdit={setEditingTemplate}
              onToggle={async (template) => {
                try {
                  const updatedTemplate = { ...template, active: !template.active };
                  const updatedTemplates = templates.map(t =>
                    t._id === template._id ? updatedTemplate : t
                  );
                  setTemplates(updatedTemplates);
                  onSuccess('Template status updated successfully');
                } catch (error) {
                  onError('Failed to update template status');
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailConfig;